import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, Phone, Bike } from 'lucide-react';

const ModernNavigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  const menuStructure = [
    {
      name: 'Location',
      items: [
        { name: 'Location dans votre camping', path: '/reserver' },
        { name: 'Livraison de vélos', path: '/demande-livraison' },
        { name: 'Groupes & séminaires', path: '/demande-groupe' }
      ]
    },
    {
      name: 'Nos vélos',
      items: [
        { name: 'Vélos électriques', path: '/velos-electriques' },
        { name: 'Vélos classiques', path: '/velos-classiques' },
        { name: 'Vélos enfants', path: '/velos-enfants' },
        { name: 'Vélos reconditionnés', path: '/velos-occasion' },
        { name: 'Recherche personnalisée', path: '/nous-contacter' }
      ]
    },
    {
      name: 'Destinations',
      items: [
        { 
          name: 'Île de Ré', 
          path: '/destinations/ile-de-re',
          submenu: [
            { name: 'Rivedoux-Plage', path: '/destinations/ile-de-re/rivedoux-plage' },
            { name: 'Sainte-Marie-de-Ré', path: '/destinations/ile-de-re/sainte-marie-de-re' },
            { name: 'La Flotte', path: '/destinations/ile-de-re/la-flotte' },
            { name: 'Saint-Martin-de-Ré', path: '/destinations/ile-de-re/saint-martin-de-re' },
            { name: 'Le Bois-Plage-en-Ré', path: '/destinations/ile-de-re/le-bois-plage-en-re' },
            { name: 'La Couarde-sur-Mer', path: '/destinations/ile-de-re/la-couarde-sur-mer' },
            { name: 'Loix', path: '/destinations/ile-de-re/loix' },
            { name: 'Ars-en-Ré', path: '/destinations/ile-de-re/ars-en-re' },
            { name: 'Saint-Clément-des-Baleines', path: '/destinations/ile-de-re/saint-clement-des-baleines' },
            { name: 'Les Portes-en-Ré', path: '/destinations/ile-de-re/les-portes-en-re' }
          ]
        },
        { name: 'Vendée', path: '/destinations/vendee' },
        { name: 'Royan', path: '/destinations/royan' },
        { name: 'Soulac', path: '/destinations/soulac' },
        { name: 'Montalivet', path: '/destinations/montalivet' },
        { name: 'Les Landes', path: '/destinations/les-landes' }
      ]
    },
    {
      name: 'Conseils',
      items: [
        { name: 'Balades recommandées', path: '/balades' },
        { name: 'Qui sommes-nous', path: '/qui-sommes-nous' },
        { name: 'FAQ', path: '/faq' }
      ]
    }
  ];

  const handleMouseEnter = (menuName: string) => {
    setActiveDropdown(menuName);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/particuliers" className="flex-shrink-0 group">
            <img 
              src="/images/logo hirondelles fond transparent.jpg" 
              alt="Les Hirondelles" 
              className="w-auto transition-transform duration-300 group-hover:scale-105 h-[50px] object-cover" 
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1">
            {menuStructure.map((menu) => (
              <div
                key={menu.name}
                className="relative"
                onMouseEnter={() => handleMouseEnter(menu.name)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className="px-4 py-2 text-gray-700 hover:text-emerald-600 font-medium transition-colors flex items-center"
                >
                  {menu.name}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </button>

                {/* Dropdown Menu */}
                {activeDropdown === menu.name && (
                  <div className="absolute left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                    {menu.items.map((item) => (
                      <div key={item.path}>
                        <Link
                          to={item.path}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                        >
                          {item.name}
                        </Link>
                        {/* Submenu for Île de Ré */}
                        {item.submenu && (
                          <div className="pl-4 bg-gray-50">
                            {item.submenu.map((subitem) => (
                              <Link
                                key={subitem.path}
                                to={subitem.path}
                                className="block px-4 py-1.5 text-xs text-gray-600 hover:text-emerald-600 transition-colors"
                              >
                                {subitem.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right Side CTAs - Desktop */}
          <div className="hidden lg:flex items-center space-x-3">
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all">
              <Link to="/reserver">
                <Bike className="w-4 h-4 mr-2" />
                Louer un vélo
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 font-medium px-6 py-2.5 rounded-lg transition-all">
              <Link to="/nous-contacter">
                <Phone className="w-4 h-4 mr-2" />
                Contact
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-emerald-600 hover:bg-gray-100 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white">
          <div className="px-4 pt-2 pb-4 space-y-1">
            {menuStructure.map((menu) => (
              <div key={menu.name} className="space-y-1">
                <div className="font-semibold text-gray-900 px-3 py-2 text-sm">
                  {menu.name}
                </div>
                {menu.items.map((item) => (
                  <div key={item.path}>
                    <Link
                      to={item.path}
                      className="block px-6 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 rounded-md transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.name}
                    </Link>
                    {/* Mobile Submenu */}
                    {item.submenu && (
                      <div className="pl-4 space-y-1">
                        {item.submenu.map((subitem) => (
                          <Link
                            key={subitem.path}
                            to={subitem.path}
                            className="block px-6 py-1.5 text-xs text-gray-600 hover:text-emerald-600 transition-colors"
                            onClick={() => setIsOpen(false)}
                          >
                            {subitem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {/* Mobile CTAs */}
            <div className="pt-4 space-y-2">
              <Link to="/reserver" onClick={() => setIsOpen(false)}>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Bike className="w-4 h-4 mr-2" />
                  Louer un vélo
                </Button>
              </Link>
              <Link to="/nous-contacter" onClick={() => setIsOpen(false)}>
                <Button variant="outline" className="w-full border-emerald-300 text-emerald-600 hover:bg-emerald-50">
                  <Phone className="w-4 h-4 mr-2" />
                  Contact
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default ModernNavigation;